<script>
    import { onMount, onDestroy, tick } from "svelte";
    import * as campaignService from "../service.js";
    import { notificate } from "../../../../services/notification.js";
    import { PowerTable } from "@muonw/powertable";
    import powerTableService from "../../../../services/powerTable";
    import Form from "../_form.svelte";
    import ReportModal from "./report-modal.svelte";
    import ImportModal from "./import-modal.svelte";
    import ReportActionRow from "./report-action-row.svelte";
    import { format, locale } from "svelte-i18n";
    import * as bootstrap from "bootstrap";

    import { dateText, removeTags } from "../../../../js/utils.js";
    import { user } from "../../../user/user.state.svelte.js";

    let { data = [], onadd, ondel, onedit, ontableRefresh, ondownload } = $props();

    let selected_rows = $state([]);
    let checkbox = $state();
    let tableElement = $state();
    let reload_edit_modal = $state(false);
    let reload_report_modal = $state(false);
    let reload_import_modal = $state(false);
    let selectedCampaignForReport = $state(null);
    let selectedCampaignForImport = $state(null);
    let ptData = $state(JSON.parse(JSON.stringify(data)));
    let tooltip = $state();
    let is_requesting = $state(false);

    // Modal Instances & Elements (matching the working example)
    let addModalElement;
    let addModalInstance;
    let editModalElement;
    let editModalInstance;
    let reportModalElement;
    let reportModalInstance;
    let importModalElement;
    let importModalInstance;

    let ptInstructs = [
        { key: "select", title: "", filterable: false, sortable: false },
        { key: "name", title: $format("phishing.campaign.name"), filterable: false },
        {
            key: "description",
            title: $format("phishing.campaign.description"),
            filterable: false,
        },
        {
            key: "sent_count",
            title: $format("phishing.campaign.sent_count"),
            filterable: false,
            parseAs: "unsafe-html",
        },
        {
            key: "opened_count",
            title: $format("phishing.campaign.opened_count"),
            filterable: false,
            parseAs: "unsafe-html",
        },
        {
            key: "clicked_count",
            title: $format("phishing.campaign.clicked_count"),
            filterable: false,
            parseAs: "unsafe-html",
        },
        {
            key: "submitted_count",
            title: $format("phishing.campaign.submitted_count"),
            filterable: false,
            parseAs: "unsafe-html",
        },
        {
            key: "reported_count",
            title: $format("phishing.campaign.reported_count"),
            filterable: false,
            parseAs: "unsafe-html",
        },
        {
            key: "created_at",
            title: $format("phishing.campaign.created_at"),
            filterable: false,
            parseAs: "unsafe-html",
        },
        {
            key: "actions",
            title: $format("phishing.campaign.actions"),
            filterable: false,
            sortable: false,
            parseAs: "component",
            dataComponent: ReportActionRow,
        },
    ];

    let ptOptions = $state({
        uniquePrefix: "campaignTable",
        rowsPerPageOptions: [10, 20, 50, 100],
        rowsPerPage: 10,
        footerText: false,
        footerFilters: false,
        footerLoadingBar: false,
        segments: {
            topBar: ["search"],
            pTable: ["table"],
            bottomBar: ["dropdown", "stats", "pagination"],
        },
        translations: {},
        isDataRemote: false,
        userFunctions: { customParse: parseRows },
    });

    $effect(() => {
        ptData = JSON.parse(JSON.stringify(data));
    });

    $effect(() => {
        if ($locale) {
            ptOptions.translations = {
                add: $format("phishing.campaign.add"),
                delete: $format("phishing.campaign.delete"),
                filterBy: $format("list.interface.filterBy"),
                from: $format("list.interface.from"),
                invertSelection: $format("list.interface.invertSelection"),
                next: $format("list.interface.next"),
                of: $format("list.interface.of"),
                previous: $format("list.interface.previous"),
                rows: $format("list.interface.rows"),
                search: $format("list.interface.search"),
                selectAll: $format("list.interface.selectAll"),
                selectNone: $format("list.interface.selectNone"),
            };
        }
    });

    // --- NEW MODAL CONTROLLERS ---

    const openAddModal = (e) => {
        if (e) e.preventDefault();
        addModalInstance.show();
    };

    const closeAddModal = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        addModalInstance.hide();
    };

    const handleEdit = async () => {
        if (selected_rows.length < 1)
            return notificate("select_element", "modal");
        if (selected_rows.length > 1)
            return notificate("select_only_one", "modal");
        reload_edit_modal = !reload_edit_modal; // Trashes form state cleanly
        await tick();
        editModalInstance.show();
    };

    const closeEditModal = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        editModalInstance.hide();
    };

    const openReportModal = async (campaign) => {
        selectedCampaignForReport = campaign;
        reload_report_modal = !reload_report_modal;
        await tick();
        reportModalInstance.show();
    };

    const closeReportModal = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        reportModalInstance.hide();
    };

    const openImportModal = async (campaign) => {
        selectedCampaignForImport = campaign;
        reload_import_modal = !reload_import_modal;
        await tick();
        importModalInstance.show();
    };

    const closeImportModal = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        importModalInstance.hide();
    };

    const handleImportSuccess = async (result, campaignId) => {
        if (ontableRefresh) {
            ontableRefresh();
        }
    };

    const handleAdd = async (campaign) => {
        closeAddModal();
        closeEditModal();

        await tick();

        setTimeout(() => {
            if (onadd) onadd(campaign);
        }, 400);
    };

    const handleEditFn = async (campaign) => {
        if (onedit) onedit(campaign);
        closeAddModal();
        closeEditModal();
    };

    const handleDownloadReport = async (campaignId) => {
        if (ondownload) ondownload(campaignId);
    };

    // --- REST OF FUNCTIONS ---

    function parseRows(pageContent) {
        pageContent.forEach((row) => {
            if (row["created_at"]) {
                let created_at = row.created_at;
                row["created_at"] =
                    `<span title="${created_at}">${dateText(row["created_at"])}</span>`;
            }
            const sent = row.sent_count ?? 0;
            const opened = row.opened_count ?? 0;
            const clicked = row.clicked_count ?? 0;
            const submitted = row.submitted_count ?? 0;
            const reported = row.reported_count ?? 0;

            row.sent_count = `<div class="text-center"><span class="cvss-badge" style="background: #154889 !important; display: inline-block;  max-width: 100px !important;  padding: 4px 0 2px;">${sent}</span></div>`;
            row.opened_count = `<div class="text-center"><span class="cvss-badge" style="background-color: rgba(212, 101, 47, 0.9) !important; color: #fff; display: inline-block;  max-width: 100px !important;  padding: 4px 0 2px;">${opened}</span></div>`;
            row.clicked_count = `<div class="text-center"><span class="cvss-badge bg-danger" style="background-color: rgba(160, 33, 40, 0.9) !important; display: inline-block;  max-width: 100px !important;  padding: 4px 0 2px;">${clicked}</span></div>`;
            row.submitted_count = `<div class="text-center"><span class="cvss-badge" style="background-color: rgba(144, 70, 132, 0.9) !important; color: #fff; display: inline-block;  max-width: 100px !important;  padding: 4px 0 2px;">${submitted}</span></div>`;
            row.reported_count = `<div class="text-center"><span class="cvss-badge bg-success" style="display: inline-block;  max-width: 100px !important;  padding: 4px 0 2px;">${reported}</span></div>`;

            row.actions = {
                campaign: row,
                openReportModal,
                openImportModal,
            };
        });
        deselectAll();
        return pageContent;
    }

    let handleDelete = async () => {
        let result = await notificate("confirm_delete", "modal", {
            massive: true,
            findings_warning: false,
        });
        if (!result.isConfirmed) return false;

        is_requesting = true;
        let ids = selected_rows.map((row) => row.id);
        let campaignsToDelete = data.filter((campaign) =>
            ids.includes(campaign.id),
        );

        campaignsToDelete.forEach((campaign, i) => {
            campaignService
                .del(campaign)
                .then(() => {
                    if (i == campaignsToDelete.length - 1) {
                        is_requesting = false;
                        if (ondel) ondel(campaign);
                    }
                })
                .catch((errors) => {
                    is_requesting = false;
                    if (ondel) ondel(campaign);
                    return errors;
                });
        });
    };

    const selectAll = async (e) => {
        let tData = tableElement.getData(false, ["pageData", "matchedData"]);
        let pageData = tData.pageData;
        if (!pageData || pageData.length === 0) return selected_rows;

        const selectedIds = new Set(selected_rows.map((r) => r.id));
        const allPageSelected = pageData.every((el) => selectedIds.has(el.id));
        let action = allPageSelected ? "deselect" : "select";

        if (action === "select") {
            let result;
            if (tData.matchedData.length > 10) {
                result = await notificate("select_all", "modal", {});
                if (!result.isConfirmed && result.dismiss !== "cancel")
                    return selected_rows;
            }

            let target_elements =
                result && result.isConfirmed ? tData.matchedData : pageData;
            const currentIds = new Set(selected_rows.map((x) => x.id));
            let toAdd = target_elements.filter((x) => !currentIds.has(x.id));
            selected_rows = [...selected_rows, ...toAdd];
        } else {
            selected_rows = [];
        }

        powerTableService.toggleActionButtons(selected_rows);
        const domRows = document.querySelectorAll(
            `div[data-prefix='campaignTable'] table tbody tr`,
        );
        const selectedPtIds = new Set(
            selected_rows.map((r) => String(r.__PT_ID__)),
        );

        domRows.forEach((row) => {
            if (selectedPtIds.has(row.getAttribute("data-id")))
                row.classList.add("selected");
            else row.classList.remove("selected");
        });

        return selected_rows;
    };

    const deselectAll = () => {
        selected_rows = powerTableService.deselectAll();
        powerTableService.toggleActionButtons(selected_rows);
        return selected_rows;
    };

    const selectAllEventListener = () => {
        checkbox = document.querySelector(
            "th[data-key='select'][data-sortable='false']",
        );
        if (checkbox) checkbox.addEventListener("click", selectAll);
    };

    const selectRow = (event) => {
        let tData = tableElement.getData(false, ["pageData"]);
        let current = event.detail.data;
        let idx = selected_rows.findIndex((r) => r.id == current.id);
        selected_rows = powerTableService.selectRows(
            selected_rows,
            current,
            idx,
            "campaignTable",
            tData.pageData,
        );
        powerTableService.toggleActionButtons(selected_rows);
        return selected_rows;
    };

    const exportToJSON = (e) => {
        let currentData = tableElement.getData(
            false,
            "matchedData",
        ).matchedData;
        for (let item of currentData) {
            delete item.__PT_ID__;
            delete item.__PT_CB__;
            delete item.actions;
        }

        let dataStr =
            "data:text/json;charset=utf-8," +
            encodeURIComponent(JSON.stringify(currentData));
        let downloadAnchorNode = document.createElement("a");
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute(
            "download",
            `CampanasPhishing_vulseek.json`,
        );
        document.body.appendChild(downloadAnchorNode);
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    };

    const exportToCSV = async () => {
        let header = [];
        let titles = [];
        const tData = tableElement.getData(false, "matchedData").matchedData;
        let items = JSON.parse(JSON.stringify(tData));
        const replacer = (key, value) => {
            if (value === null || value === undefined) return "";
            if (typeof value === "string" && value.includes("<")) return removeTags(value);
            return value;
        };

        ptInstructs.forEach((col) => {
            if (col.key != "select" && col.key != "actions")
                header.push({ key: col.key, value: col.title });
            if (col.title && col.title.includes(" "))
                col.title = col.title.replace(" ", "_");
        });

        const csv = [
            titles.join(";"),
            ...items.map((row) =>
                header
                    .map((fieldName) =>
                        JSON.stringify(row[fieldName["key"]], replacer),
                    )
                    .join(";"),
            ),
        ].join("\r\n");

        let hiddenElement = document.createElement("a");
        hiddenElement.href =
            "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
        hiddenElement.target = "_blank";
        hiddenElement.download = `CampanasPhishing_vulseek.csv`;
        hiddenElement.click();
    };

    onMount(async () => {
        // Init Modals using Bootstrap JS and Svelte bindings!
        addModalInstance = new bootstrap.Modal(addModalElement);
        editModalInstance = new bootstrap.Modal(editModalElement);
        reportModalInstance = new bootstrap.Modal(reportModalElement);
        importModalInstance = new bootstrap.Modal(importModalElement);

        await tick();
        tooltip = document.getElementById("tooltip-response");
        selectAllEventListener();
    });

    onDestroy(() => {
        if (checkbox) checkbox.removeEventListener("click", selectAll);

        // Clean up to be safe
        document
            .querySelectorAll(".modal-backdrop")
            .forEach((el) => el.remove());
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("padding-right");
        document.body.style.removeProperty("overflow");
    });
</script>

<div class="row">
    <div class="col-12">
        <div class="card">
            <div class="card-header">
                <div class="float-end">
                    {#if user.data.role != "viewer"}
                        <button
                            class="btn btn-secondary table-button btn-add btn-xs"
                            tabindex="0"
                            aria-controls="campaignTable"
                            type="button"
                            title={$format("phishing.campaign.add")}
                            id="campaignAddBtn"
                            onclick={openAddModal}
                            ><i class="fa fa-plus"></i></button
                        >

                        <button
                            disabled
                            class="btn btn-secondary btn-disabled table-button btn-delete btn-xs"
                            tabindex="0"
                            aria-controls="campaignTable"
                            type="button"
                            title={$format("phishing.campaign.delete")}
                            id="campaignDeleteBtn"
                            onclick={handleDelete}
                            ><i class="fa fa-trash"></i></button
                        >

                        <button
                            disabled
                            class="btn btn-secondary btn-disabled table-button btn-edit btn-xs"
                            tabindex="0"
                            aria-controls="campaignTable"
                            type="button"
                            title={$format("phishing.campaign.edit")}
                            id="campaignEditBtn"
                            onclick={handleEdit}
                            ><i class="fa fa-edit"></i></button
                        >
                    {/if}
                    <button
                        class="btn btn-secondary table-button btn-refresh btn-xs"
                        tabindex="0"
                        aria-controls="campaignTable"
                        type="button"
                        title={$format("buttons.refresh")}
                        id="campaignRefreshBtn"
                        onclick={() => {
                            if (ontableRefresh) ontableRefresh();
                        }}><i class="fa fa-sync-alt"></i></button
                    >

                    <button
                        class="btn btn-secondary table-button btn-export btn-xs"
                        tabindex="0"
                        aria-controls="campaignTable"
                        type="button"
                        title={$format("buttons.json")}
                        id="campaignJSONbtn"
                        onclick={exportToJSON}
                        ><i class="fab fa-js-square" style="font-size: 18px;"
                        ></i></button
                    >

                    <button
                        class="btn btn-secondary table-button btn-export btn-xs"
                        tabindex="0"
                        aria-controls="campaignTable"
                        type="button"
                        title={$format("buttons.csv")}
                        id="campaignCSVbtn"
                        onclick={exportToCSV}
                        ><i class="fa fa-file-csv" style="font-size: 18px;"
                        ></i></button
                    >
                </div>
            </div>
            <div class="card-body">
                <div class="table-responsive">
                    <div class="overlay-wrapper">
                        <div class="text-center" class:overlay={is_requesting}>
                            <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                        </div>
                    </div>
                    <PowerTable
                        {ptData}
                        {ptOptions}
                        {ptInstructs}
                        on:rowClicked={(e) => selectRow(e)}
                        bind:this={tableElement}
                    >
                        <div slot="noResults">
                            {$format("results_not_found")}
                        </div>
                    </PowerTable>
                </div>
            </div>
        </div>
    </div>
</div>

<!-- ADD MODAL -->
<div
    class="modal fade"
    id="campaign-add-modal"
    tabindex="-1"
    aria-hidden="true"
    bind:this={addModalElement}
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{$format("phishing.campaign.add")}</h4>
                <button
                    type="button"
                    class="btn-close"
                    onclick={closeAddModal}
                    aria-label="Close"
                ></button>
            </div>
            <div class="modal-body">
                <Form onsave={handleAdd} oncancel={closeAddModal} />
            </div>
        </div>
    </div>
</div>

<!-- EDIT MODAL -->
<div
    class="modal fade"
    id="campaign-edit-modal"
    tabindex="-1"
    aria-hidden="true"
    bind:this={editModalElement}
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{$format("phishing.campaign.edit")}</h4>
                <button
                    type="button"
                    class="btn-close"
                    onclick={closeEditModal}
                    aria-label="Close"
                ></button>
            </div>
            <div class="modal-body">
                {#key reload_edit_modal}
                    <Form
                        campaign={selected_rows[0]}
                        onsave={handleEditFn}
                        oncancel={closeEditModal}
                    />
                {/key}
            </div>
        </div>
    </div>
</div>

<!-- REPORT MODAL -->
<div
    class="modal fade"
    id="campaign-report-modal"
    tabindex="-1"
    aria-hidden="true"
    bind:this={reportModalElement}
>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            {#key reload_report_modal}
                <ReportModal
                    campaign={selectedCampaignForReport}
                    onclose={closeReportModal}
                />
            {/key}
        </div>
    </div>
</div>

<!-- IMPORT MODAL -->
<div
    class="modal fade"
    id="campaign-import-modal"
    tabindex="-1"
    aria-hidden="true"
    bind:this={importModalElement}
>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            {#key reload_import_modal}
                <ImportModal
                    campaign={selectedCampaignForImport}
                    onclose={closeImportModal}
                    onimportsuccess={handleImportSuccess}
                />
            {/key}
        </div>
    </div>
</div>

<style>
    .overlay-wrapper div {
        display: none;
    }
    .overlay-wrapper div.overlay {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        margin-top: 20px;
    }
</style>